// Pure state machine for Field PWA foreground-time tracking (card #4838).
//
// No imports, no DOM, no storage, no logging -- every function here is a pure
// transition: (state, inputs) -> state (or a small derived value). This is
// deliberate so it can be driven with fake monotonic and wall clocks and
// exercised directly under plain Node, the same way the rest of this app's
// JS is "tested" where there is no browser-based JS unit runner (see
// test/javascript/field_speed_events_contract_test.rb's header comment).
// test/javascript/field_foreground_time_test.rb requires this file with
// `node -e` and asserts on real behaviour -- sums across hide/show,
// checkpoint recovery, area totals, the 12h clamp.
//
// field_foreground_time.js is the thin browser-facing shell: it owns the
// actual visibilitychange/pagehide/pageshow/freeze/resume listeners, the
// localStorage checkpoint, the 60s timer, and the logEvent calls, all built
// on top of the transitions below.
//
// Two clocks, deliberately:
//   - perfMs: a monotonic clock (performance.now() in the browser) -- used
//     for every DURATION (segment length). Immune to the OS clock being
//     changed mid-session.
//   - wallMs: a wall clock (Date.now() in the browser) -- used only to know
//     which LOCAL DAY it is, so "since local midnight" resets correctly and
//     a segment that spans midnight is split at the boundary.

export const MAX_SEGMENT_MS = 12 * 60 * 60 * 1000 // 12h clamp (note 2)
export const CHECKPOINT_INTERVAL_MS = 60 * 1000 // write a checkpoint every 60s while visible
export const FOREGROUND_EVENT_MIN_INTERVAL_MS = 10 * 60 * 1000 // "Foreground time" event, at most every 10 min
export const MAX_AREA_KEYS = 10

// Phase 2 (note 3): fixed, closed set of area labels -- kept at or under
// MAX_AREA_KEYS by construction. "scanner" and "camera" are never derived
// from the route; they're pushed/popped as overrides while an in-app camera
// or barcode scanner sheet is open (see field_foreground_time.js).
export function areaForPath(pathname) {
  const path = pathname || ""
  if (/^\/field\/responses(\/|$)/.test(path)) return "survey"
  if (/^\/field\/places(\/|$)/.test(path)) return "places"
  if (/^\/field\/sync(\/|$)/.test(path)) return "sync"
  if (/^\/field\/photos(\/|$)/.test(path)) return "photos"
  if (/^\/field\/(settings|more)(\/|$)/.test(path)) return "settings"
  if (path === "/field" || path === "/field/" || /^\/field\/today(\/|$)/.test(path)) return "home"
  return "other"
}

export function dayKey(wallMs) {
  const d = new Date(wallMs)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export function initialState(wallMs) {
  return {
    day: dayKey(wallMs),
    dayMs: 0,
    daySegments: 0,
    byArea: {},
    visible: false,
    segmentStartPerf: null,
    segmentStartWall: null,
    currentArea: "other"
  }
}

// Restore a previously-persisted checkpoint (see persistable() below). A
// stored checkpoint from a different local day is discarded -- "since local
// midnight" always starts at zero, never carries a balance across days. The
// restored state is never "visible" -- a fresh load hasn't seen a
// visibilitychange yet, so field_foreground_time.js decides that separately
// from document.visibilityState. This is the "add the checkpoint once" step
// (note 2): it is read and folded into the fresh in-memory state exactly
// once, at init -- nothing re-reads or re-applies it afterwards.
export function loadCheckpoint(stored, wallMs) {
  if (!stored || typeof stored !== "object" || stored.day !== dayKey(wallMs)) {
    return initialState(wallMs)
  }
  return {
    day: stored.day,
    dayMs: Number(stored.dayMs) > 0 ? Number(stored.dayMs) : 0,
    daySegments: Number(stored.daySegments) > 0 ? Number(stored.daySegments) : 0,
    byArea: stored.byArea && typeof stored.byArea === "object" ? { ...stored.byArea } : {},
    visible: false,
    segmentStartPerf: null,
    segmentStartWall: null,
    currentArea: "other"
  }
}

// What gets written to storage every 60s. Deliberately excludes the live
// perf/wall segment-start timestamps -- performance.now() is only meaningful
// within the document that produced it, and is never relevant to read back
// after a reload.
export function persistable(state) {
  return { day: state.day, dayMs: state.dayMs, daySegments: state.daySegments, byArea: { ...state.byArea } }
}

export function snapshot(state) {
  return {
    fgDayMs: state.dayMs,
    fgDaySegments: state.daySegments,
    fgVisibleNow: state.visible,
    fgByArea: capByArea(state.byArea)
  }
}

// Cap to MAX_AREA_KEYS entries, folding the smallest overflow into "other" --
// the sum of values is always preserved exactly, so fgByArea always sums to
// fgDayMs even if more than MAX_AREA_KEYS areas were ever seen in a day.
function capByArea(byArea, limit = MAX_AREA_KEYS) {
  const entries = Object.entries(byArea).filter(([, ms]) => ms > 0)
  if (entries.length <= limit) return { ...byArea }

  entries.sort((a, b) => b[1] - a[1])
  const kept = entries.slice(0, limit - 1)
  const overflow = entries.slice(limit - 1).reduce((sum, [, ms]) => sum + ms, 0)
  const result = Object.fromEntries(kept)
  result.other = (result.other || 0) + overflow
  return result
}

// Fold the time elapsed since segmentStartPerf into dayMs/byArea, clamped to
// MAX_SEGMENT_MS. A no-op (0ms) when not currently visible.
function closeSegment(state, perfMs) {
  if (!state.visible || state.segmentStartPerf == null) return { state, elapsedMs: 0 }

  const raw = perfMs - state.segmentStartPerf
  const elapsed = Math.max(0, Math.min(raw, MAX_SEGMENT_MS))
  const area = state.currentArea || "other"
  const byArea = { ...state.byArea, [area]: (state.byArea[area] || 0) + elapsed }

  return {
    state: { ...state, dayMs: state.dayMs + elapsed, byArea, visible: false, segmentStartPerf: null, segmentStartWall: null },
    elapsedMs: elapsed
  }
}

// Roll over to a new local day if wallMs has crossed local midnight since
// state.day was set. If a segment is open when the boundary is crossed, it is
// split there: the portion before midnight belongs to the day being retired
// (and is discarded -- only "today" is ever kept), the portion from midnight
// onward re-opens as the first segment of the new day. This is what keeps
// "foreground time today" from ever exceeding the time actually elapsed
// since local midnight.
function rollDayIfNeeded(state, wallMs) {
  const today = dayKey(wallMs)
  if (today === state.day) return state

  if (state.visible && state.segmentStartPerf != null && state.segmentStartWall != null) {
    const midnightWall = new Date(wallMs)
    midnightWall.setHours(0, 0, 0, 0)
    const midnightPerf = state.segmentStartPerf + (midnightWall.getTime() - state.segmentStartWall)
    return {
      day: today,
      dayMs: 0,
      daySegments: 1,
      byArea: {},
      visible: true,
      segmentStartPerf: midnightPerf,
      segmentStartWall: midnightWall.getTime(),
      currentArea: state.currentArea
    }
  }

  return { day: today, dayMs: 0, daySegments: 0, byArea: {}, visible: false, segmentStartPerf: null, segmentStartWall: null, currentArea: state.currentArea }
}

// The app became visible (foreground). Starts a new segment and counts it.
// A no-op if already visible (defensive -- callers should only fire this on
// a real hidden -> visible transition).
export function onVisible(state, { perfMs, wallMs, area }) {
  const rolled = rollDayIfNeeded(state, wallMs)
  if (rolled.visible) return rolled

  return {
    ...rolled,
    visible: true,
    segmentStartPerf: perfMs,
    segmentStartWall: wallMs,
    currentArea: area || rolled.currentArea,
    daySegments: rolled.daySegments + 1
  }
}

// The app became hidden (backgrounded, bfcache freeze, or pagehide). Folds
// the open segment into the totals. A no-op if already hidden.
export function onHidden(state, { perfMs, wallMs }) {
  const rolled = rollDayIfNeeded(state, wallMs)
  return closeSegment(rolled, perfMs).state
}

// Flush the open segment into the totals and immediately re-open it from now
// -- used for BOTH the periodic 60s checkpoint and a mid-visit area change
// (survey -> sync, say). This is the "add the checkpoint once" write: because
// each flush re-opens the segment from the flush point, a checkpoint read
// back after a kill never double-counts the span it already covered. Passing
// a new `area` records the change (e.g. the Tasker navigated); omitting it
// keeps the current area. A no-op beyond updating currentArea when hidden.
export function flush(state, { perfMs, wallMs, area }) {
  const rolled = rollDayIfNeeded(state, wallMs)
  if (!rolled.visible) return { ...rolled, currentArea: area || rolled.currentArea }

  const { state: closed } = closeSegment(rolled, perfMs)
  return {
    ...closed,
    visible: true,
    segmentStartPerf: perfMs,
    segmentStartWall: wallMs,
    currentArea: area || rolled.currentArea
  }
}

// Throttle decision for the "Foreground time" event (note 3): at most once
// every FOREGROUND_EVENT_MIN_INTERVAL_MS, except when force is set (shift
// end always logs). lastEmittedWallMs is null the first time this ever runs.
export function shouldEmitForegroundEvent(lastEmittedWallMs, nowWallMs, { force = false } = {}) {
  if (force) return true
  if (lastEmittedWallMs == null) return true
  return (nowWallMs - lastEmittedWallMs) >= FOREGROUND_EVENT_MIN_INTERVAL_MS
}
